const REQUIRED_FIELD_MESSAGE = 'This is a required field';
const NO_MATCHING_START_MESSAGE = 'There is no matching start time with this end time';
const NO_MATCHING_END_MESSAGE = 'There is no matching end time with this start time';
const INVALID_SEGMENT_LENGTH = 'Segments must be at least 1 minute long';
const SEGMENTS_RANGE_ERROR_MESSAGE = "Segments must not exceed the shift's scheduled time.";
const DUPLICATED_SEGMENTS_MESSAGE = 'Duplicate segments cannot be added.';
export const INVALID_TIME_MESSAGE = 'Enter a valid time';
export const NO_UNIT_LABEL = '-';

export const defaultSegments = (shiftStart, shiftEnd) => [
	{ start: shiftStart.clone(), end: null, unitId: null },
	{ start: null, end: shiftEnd.clone(), unitId: null }
];

export const sortSegments = (segments) =>
	[...segments].sort((a, b) => {
		if (!a.start || !b.start) {
			return 0;
		}
		return a.start.valueOf() - b.start.valueOf();
	});

// Editing can go down to a single segment (that reverts the shift to non-split), but a split
// being created must always keep at least 2 segments, so deleting needs a third one to spare.
export const canDeleteSegment = (index, total, isEditing) => {
	if (index === 0) {
		return false;
	}
	return isEditing ? total >= 2 : total >= 3;
};

// The first segment's start picker and the last segment's end picker are disabled in the UI —
// a split always covers the whole shift, so those two ends are never the user's to edit. Every
// add/delete runs through here so the invariant holds no matter how the rows were rearranged.
export const reanchorBoundaries = (segments, shiftStart, shiftEnd) => {
	const lastIndex = segments.length - 1;
	return segments.map((segment, index) => {
		const reanchored = { ...segment };
		if (index === 0) {
			reanchored.start = shiftStart.clone();
		}
		if (index === lastIndex) {
			reanchored.end = shiftEnd.clone();
		}
		return reanchored;
	});
};

export const validateSegments = (segments, shiftStart, shiftEnd) => {
	const errors = {};
	const sorted = sortSegments(segments.map((segment, originalIndex) => ({ ...segment, originalIndex })));

	sorted.forEach((segment) => {
		if (!segment.start || !segment.start.isValid()) {
			errors[`segments[${segment.originalIndex}].startTime`] = REQUIRED_FIELD_MESSAGE;
		}
		if (!segment.end || !segment.end.isValid()) {
			errors[`segments[${segment.originalIndex}].endTime`] = REQUIRED_FIELD_MESSAGE;
		}
	});

	if (Object.keys(errors).length > 0) {
		return { isValid: false, errors, rangeError: null };
	}

	const startValues = sorted.map((segment) => segment.start.valueOf());
	const endValues = sorted.map((segment) => segment.end.valueOf());

	sorted.forEach((segment) => {
		const startValue = segment.start.valueOf();
		const endValue = segment.end.valueOf();
		const isFirst = segment.originalIndex === 0;
		const isLast = segment.originalIndex === sorted.length - 1;
		if (!isFirst && !endValues.includes(startValue)) {
			errors[`segments[${segment.originalIndex}].startTime`] = NO_MATCHING_END_MESSAGE;
		}
		if (!isLast && !startValues.includes(endValue)) {
			errors[`segments[${segment.originalIndex}].endTime`] = NO_MATCHING_START_MESSAGE;
		}

		if (!isFirst && segment.start.isSame(segment.end)) {
			errors[`segments[${segment.originalIndex}].startTime`] = INVALID_SEGMENT_LENGTH;
		}
		if (!isLast && segment.start.isSame(segment.end)) {
			errors[`segments[${segment.originalIndex}].endTime`] = INVALID_SEGMENT_LENGTH;
		}
	});

	let rangeError = null;
	if (Object.keys(errors).length === 0) {
		const exceedsShiftEnd = sorted.some(
			(segment) => segment.end.isAfter(shiftEnd) || segment.start.isAfter(segment.end)
		);

		if (exceedsShiftEnd) {
			rangeError = SEGMENTS_RANGE_ERROR_MESSAGE;
		}
		const hasDuplicates =
			new Set(sorted.map((segment) => `${segment.start.valueOf()}-${segment.end.valueOf()}`)).size < sorted.length;

		if (hasDuplicates) {
			rangeError = DUPLICATED_SEGMENTS_MESSAGE;
		}
	}

	return { isValid: Object.keys(errors).length === 0 && !rangeError, errors, rangeError };
};

export const toPayload = (segments) =>
	segments.map((segment) => ({
		startTime: segment.start ? segment.start.format('HH:mm') : null,
		endTime: segment.end ? segment.end.format('HH:mm') : null,
		unitId: segment.unitId || null
	}));

export const formatRange = (segment) => {
	const start = segment.start ? segment.start.format('h:mm A [(]ddd[)]') : '';
	const end = segment.end ? segment.end.format('h:mm A [(]ddd[)]') : '';
	return `${start} – ${end}`;
};

// facility_units.id is a bigint, so it arrives from the API as a string. The Global Overview
// normalizes it to a Number before passing units down and the Daily Schedule does not, while
// segment unitIds are always parsed to Number — so compare numerically rather than strictly.
export const resolveUnitName = (units, unitId) => {
	if (!unitId) {
		return NO_UNIT_LABEL;
	}
	const unit = units.find((item) => Number(item.id) === Number(unitId));
	return unit ? unit.unit_name : NO_UNIT_LABEL;
};

// segment_unit_ids arrives ordered by segment_number, with a null entry for a segment that has no
// unit — those are left out of the list rather than rendered as a placeholder. Names are looked up
// through a Map so the cost stays linear in units + segments. Returns '' when no segment has a unit.
export const resolveSegmentUnitNames = (units, segmentUnitIds = []) => {
	const unitNamesById = new Map(units.map((unit) => [Number(unit.id), unit.unit_name]));
	return segmentUnitIds
		.map((unitId) => (unitId ? unitNamesById.get(Number(unitId)) : null))
		.filter(Boolean)
		.join(', ');
};

const isSameSegmentTime = (left, right) => left.start.isSame(right.start) && left.end.isSame(right.end);

export const buildDiffRows = (originalSegments, newSegments, units) => {
	const rowCount = Math.max(originalSegments.length, newSegments.length);

	return Array.from({ length: rowCount }, (_value, index) => {
		const original = originalSegments[index];
		const updated = newSegments[index];
		const segmentNumber = index + 1;

		if (!updated) {
			return {
				segmentNumber,
				status: 'removed',
				time: null,
				previousTime: formatRange(original),
				unit: null,
				previousUnit: resolveUnitName(units, original.unitId)
			};
		}

		if (!original) {
			return {
				segmentNumber,
				status: 'added',
				time: formatRange(updated),
				previousTime: null,
				unit: resolveUnitName(units, updated.unitId),
				previousUnit: null
			};
		}

		const timeChanged = !isSameSegmentTime(original, updated);
		const unitChanged = (original.unitId || null) !== (updated.unitId || null);

		return {
			segmentNumber,
			status: timeChanged || unitChanged ? 'changed' : 'unchanged',
			time: formatRange(updated),
			previousTime: timeChanged ? formatRange(original) : null,
			unit: resolveUnitName(units, updated.unitId),
			previousUnit: unitChanged ? resolveUnitName(units, original.unitId) : null
		};
	});
};
